/**
 * useNativeRitualVideos (issue #362)
 *
 * Encapsulates the native (Cloudflare-hosted) ritual video pipeline for the
 * edit-page media section: lists existing videos, uploads new ones through
 * the GCS -> Cloudflare Stream flow, deletes, and tracks live status via
 * Ably. The media section bridges the returned `videos` into its unified
 * grid as cards so video sits alongside images + audio with one upload
 * entry point -- while staying on the Cloudflare pipeline under the hood,
 * separate from develop's RitualMedia/uploads table.
 *
 * Deferred create: native upload needs an existing ritualId, which the CREATE
 * flow doesn't have yet. When called without a ritualId, uploadVideo QUEUES
 * the file (shown as a card) instead of erroring; the create page calls
 * flushPending(newRitualId) after the ritual is created to upload the queue.
 *
 * Drag-reorder of video cards across the two tables is out of scope here
 * (tracked in #429); this hook owns display + upload + delete + status.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { useRealtimeChannelEvent } from "@/contexts/RealtimeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { looksLikeVideoFile } from "@/utils/fileTypes"
import {
  confirmVideoUpload,
  deleteRitualVideo,
  listRitualVideos,
  type RitualVideoStatus,
  requestVideoUploadUrl,
  updateRitualVideo,
} from "../services/ritual.service"
import { isAbortError, probeVideoDuration, uploadResumable } from "../utils/gcsResumableUpload"
import { validateAndProbeVideo } from "../utils/videoGates"

// "queued" = picked during create (no ritualId yet); the actual upload is
// deferred to flushPending once the ritual exists. Distinct from "uploading"
// so the card doesn't misleadingly show "Uploading 0%" while idle.
export type NativeVideoStatus = RitualVideoStatus | "uploading" | "queued" | "error"

export interface NativeVideoItem {
  /** Stable local id (also used while pending a server id). */
  localId: string
  /** Server-issued ritual_videos id once the row exists. */
  ritualVideoId: string | null
  status: NativeVideoStatus
  filename: string
  /** User-specified display title. Defaults to filename on upload. */
  title: string
  thumbnailUrl: string | null
  /** Pending thumbnail file for queued/create-flow videos (not yet on server). */
  thumbnailFile?: File
  cloudflareUid: string | null
  sortOrder: number
  uploadedBytes: number
  totalBytes: number
  errorMessage: string | null
}

export interface UseNativeRitualVideosResult {
  videos: NativeVideoItem[]
  /** Whether the given file is a video we should route through this pipeline. */
  isVideoFile: (file: File) => boolean
  /** Kick off a native video upload. Validates, uploads, confirms. Without a
   *  ritualId (create flow) it queues the file for flushPending instead. */
  uploadVideo: (file: File) => Promise<void>
  /** Delete a native video by its server id (or drop a not-yet-confirmed local row). */
  deleteVideo: (localId: string) => Promise<void>
  /** Set a user-specified display title for a native video. */
  setVideoTitle: (localId: string, title: string) => void
  /** Set a thumbnail for a native video (queued/create-flow only). */
  setVideoThumbnail: (localId: string, file: File) => void
  /** Upload any files queued during create (no ritualId) to the freshly
   *  created ritual. Called by the create page after the ritual exists. */
  flushPending: (newRitualId: string) => Promise<void>
  /** Whether there are queued (create-flow) videos awaiting flushPending. */
  hasPendingUploads: boolean
  /** Whether any video is actively uploading or queued -- the page uses this to
   *  guard against navigating away mid-upload. Owned here so the status
   *  taxonomy (incl. "queued") lives with the hook, not the consuming form. */
  hasActiveUpload: boolean
}

export function useNativeRitualVideos(ritualId: string): UseNativeRitualVideosResult {
  const [videos, setVideos] = useState<NativeVideoItem[]>([])
  // Mirror of `videos` so callbacks can read the latest list without taking it
  // as a dependency (keeps their identity stable for memoized consumers).
  const videosRef = useRef(videos)
  videosRef.current = videos
  const hasLoaded = useRef(false)
  // Files picked during create (no ritualId yet), keyed by localId. Uploaded
  // by flushPending once the ritual exists.
  const pendingFilesRef = useRef<Map<string, File>>(new Map())
  const flushingRef = useRef(false)
  const [hasPendingUploads, setHasPendingUploads] = useState(false)
  // Per-upload abort controllers keyed by localId, so concurrent uploads (the
  // picker allows multi-select) each get their own and can be cancelled
  // independently. A multi-GB upload would otherwise keep running detached on
  // unmount, burning uplink + patching a dead component.
  const uploadAbortsRef = useRef<Map<string, AbortController>>(new Map())
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      for (const controller of uploadAbortsRef.current.values()) controller.abort()
      uploadAbortsRef.current.clear()
    }
  }, [])

  const patch = useCallback((localId: string, next: Partial<NativeVideoItem>) => {
    if (!mountedRef.current) return // don't setState after unmount
    setVideos(prev => prev.map(v => (v.localId === localId ? { ...v, ...next } : v)))
  }, [])

  // Progress-only update that bails when the byte count is unchanged, so the
  // 250ms synthetic-progress ticks don't force a full media-grid re-render on
  // every tick (a multi-GB upload would otherwise re-render ~thousands of times,
  // many of them no-ops once the tween pins at its per-chunk cap).
  const patchProgress = useCallback((localId: string, uploaded: number) => {
    if (!mountedRef.current) return
    setVideos(prev => {
      const idx = prev.findIndex(v => v.localId === localId)
      if (idx === -1 || prev[idx].uploadedBytes === uploaded) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], uploadedBytes: uploaded }
      return next
    })
  }, [])

  const patchByVideoId = useCallback((ritualVideoId: string, next: Partial<NativeVideoItem>) => {
    if (!mountedRef.current) return
    setVideos(prev => prev.map(v => (v.ritualVideoId === ritualVideoId ? { ...v, ...next } : v)))
  }, [])

  // Seed existing videos on mount so owners can see + remove them. The
  // hasLoaded ref dedupes (incl. StrictMode's double effect invoke); we
  // deliberately avoid a per-run "cancelled" flag, which under StrictMode
  // would block seeding entirely.
  useEffect(() => {
    if (!ritualId || hasLoaded.current) return
    hasLoaded.current = true
    listRitualVideos(ritualId)
      .then(list => {
        const existing: NativeVideoItem[] = list.map(v => ({
          localId: `existing-${v.id}`,
          ritualVideoId: v.id,
          status: v.status,
          filename: v.title || "Untitled",
          title: v.title || "Untitled",
          thumbnailUrl: v.thumbnailUrl,
          cloudflareUid: v.cloudflareUid,
          sortOrder: v.sortOrder,
          uploadedBytes: 1,
          totalBytes: 1,
          errorMessage: v.failureReason,
        }))
        setVideos(prev => {
          const seen = new Set(
            prev.map(v => v.ritualVideoId).filter((id): id is string => id !== null),
          )
          // existing rows always carry a server id; the null guard is for the type.
          return [
            ...existing.filter(e => e.ritualVideoId !== null && !seen.has(e.ritualVideoId)),
            ...prev,
          ]
        })
      })
      .catch(() => {
        // Reset so a later render retries; surface it so a failed load isn't
        // mistaken for "no videos" (which could prompt a duplicate re-upload).
        hasLoaded.current = false
        if (mountedRef.current) toast.error("Couldn't load existing videos. Refresh to retry.")
      })
  }, [ritualId])

  // Live status updates for the owner's in-flight uploads. Events are fanned out
  // to the owner's identity channel (user:<id>); filter to this ritual by
  // ritualId so a multi-ritual owner only patches the videos shown here.
  const userId = useAuthStore(state => state.user?.id)
  const channel = userId ? `user:${userId}` : null
  useRealtimeChannelEvent(channel, "video-processing", payload => {
    const event = payload as { ritualVideoId?: string; ritualId?: string } | undefined
    if (event?.ritualVideoId && event.ritualId === ritualId) {
      patchByVideoId(event.ritualVideoId, { status: "processing" })
    }
  })
  useRealtimeChannelEvent(channel, "video-ready", payload => {
    const event = payload as { ritualVideoId?: string; ritualId?: string } | undefined
    if (event?.ritualVideoId && event.ritualId === ritualId) {
      patchByVideoId(event.ritualVideoId, { status: "ready" })
    }
  })
  useRealtimeChannelEvent(channel, "video-failed", payload => {
    const event = payload as
      | { ritualVideoId?: string; ritualId?: string; failureReason?: string }
      | undefined
    if (event?.ritualVideoId && event.ritualId === ritualId) {
      patchByVideoId(event.ritualVideoId, {
        status: "failed",
        errorMessage: event.failureReason ?? "Processing failed",
      })
    }
  })

  // Extension fallback so an Android .mp4 with an empty/generic MIME still
  // routes to the video pipeline instead of the image path.
  const isVideoFile = useCallback((file: File) => looksLikeVideoFile(file), [])

  // The real GCS -> confirm upload against a known ritualId.
  const performUpload = useCallback(
    async (
      localId: string,
      file: File,
      targetRitualId: string,
      duration: number,
      title?: string | null,
    ) => {
      patch(localId, { status: "uploading", uploadedBytes: 0, errorMessage: null })
      const controller = new AbortController()
      uploadAbortsRef.current.set(localId, controller)
      try {
        const signed = await requestVideoUploadUrl(targetRitualId, {
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          durationSeconds: duration > 0 ? Math.ceil(duration) : 0,
        })
        await uploadResumable({
          signedUrl: signed.signedUrl,
          file,
          contentType: file.type,
          signal: controller.signal,
          onProgress: uploaded => patchProgress(localId, uploaded),
        })
        const confirmed = await confirmVideoUpload(targetRitualId, {
          gcsPath: signed.gcsPath,
          title: title?.trim() || null,
        })
        patch(localId, { ritualVideoId: confirmed.ritualVideoId, status: confirmed.status })
        return confirmed
      } catch (error) {
        // An abort is an intentional cancel (navigation/unmount or a sibling
        // upload). Don't surface a "signal is aborted" toast. If we're still
        // mounted, move the card off "uploading" so the nav guard releases; on
        // unmount, patch is a no-op.
        if (isAbortError(error)) {
          if (mountedRef.current)
            patch(localId, { status: "error", errorMessage: "Upload cancelled" })
          throw error
        }
        const message = error instanceof Error ? error.message : "Upload failed. Please try again."
        patch(localId, { status: "error", errorMessage: message })
        throw error instanceof Error ? error : new Error(message)
      } finally {
        uploadAbortsRef.current.delete(localId)
      }
    },
    [patch, patchProgress],
  )

  const uploadVideo = useCallback(
    async (file: File) => {
      let duration: number
      try {
        duration = await validateAndProbeVideo(file)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Invalid video")
        return
      }

      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setVideos(prev => [
        ...prev,
        {
          localId,
          ritualVideoId: null,
          // Create flow has no ritualId yet -> the file is queued, not uploading.
          status: ritualId ? "uploading" : "queued",
          filename: file.name,
          title: file.name,
          thumbnailUrl: null,
          cloudflareUid: null,
          sortOrder: prev.length,
          uploadedBytes: 0,
          totalBytes: file.size,
          errorMessage: null,
        },
      ])

      // Create flow: no ritualId yet -> queue the file; flushPending uploads it
      // once the ritual exists. The card sits at "Uploading 0%" until then.
      if (!ritualId) {
        pendingFilesRef.current.set(localId, file)
        setHasPendingUploads(true)
        return
      }

      try {
        await performUpload(localId, file, ritualId, duration, undefined)
      } catch (error) {
        // Swallow intentional cancels -- a navigated-away upload must not pop a
        // "signal is aborted without reason" error toast.
        if (isAbortError(error)) return
        toast.error(error instanceof Error ? error.message : "Upload failed. Please try again.")
      }
    },
    [ritualId, performUpload],
  )

  const flushPending = useCallback(
    async (newRitualId: string) => {
      // Re-entrancy guard: a double-submit must not start two flushes that both
      // snapshot + re-upload the same queued files.
      if (flushingRef.current) return
      flushingRef.current = true
      let failed = 0
      try {
        for (;;) {
          if (!mountedRef.current) break
          const next = pendingFilesRef.current.entries().next()
          if (next.done) break
          const [localId, file] = next.value
          // Claim the entry (delete BEFORE the long upload) so a concurrent
          // flush can't re-grab the same file mid-upload.
          pendingFilesRef.current.delete(localId)
          let duration = 0
          try {
            duration = await probeVideoDuration(file)
          } catch {
            duration = 0
          }
          // Get the user-specified title from the video card.
          const videoItem = videosRef.current.find(v => v.localId === localId)
          const userTitle = videoItem?.title?.trim() || null
          const thumbnailFile = videoItem?.thumbnailFile
          try {
            const confirmed = await performUpload(localId, file, newRitualId, duration, userTitle)

            // After confirm, apply any user-set title + thumbnail via the PATCH
            // endpoint. The title is already sent to confirmVideoUpload above,
            // but a follow-up PATCH guarantees it lands even if confirm is
            // processed before the video row is ready.
            if (confirmed?.ritualVideoId && (userTitle || thumbnailFile)) {
              try {
                await updateRitualVideo(newRitualId, confirmed.ritualVideoId, {
                  title: userTitle || undefined,
                  thumbnail: thumbnailFile,
                })
              } catch {
                // Best-effort: a failed metadata update shouldn't fail the video.
              }
            }
          } catch (error) {
            // Abort (navigated away mid-flush) stops the whole queue quietly.
            if (isAbortError(error)) break
            // A real failure: the card is already marked errored; count it and
            // keep going so one bad file doesn't block the rest.
            failed++
          }
        }
        setHasPendingUploads(false)
      } finally {
        flushingRef.current = false
      }
      // Re-throw so the create page surfaces "saved, but a video failed to
      // upload" -- otherwise the failure is invisible after navigation.
      if (failed > 0) {
        throw new Error(`${failed} video${failed > 1 ? "s" : ""} failed to upload`)
      }
    },
    [performUpload],
  )

  const setVideoTitle = useCallback((localId: string, title: string) => {
    if (!mountedRef.current) return
    setVideos(prev => prev.map(v => (v.localId === localId ? { ...v, title } : v)))
  }, [])

  const setVideoThumbnail = useCallback((localId: string, file: File) => {
    if (!mountedRef.current) return
    const blobUrl = URL.createObjectURL(file)
    setVideos(prev =>
      prev.map(v =>
        v.localId === localId ? { ...v, thumbnailUrl: blobUrl, thumbnailFile: file } : v,
      ),
    )
  }, [])

  const deleteVideo = useCallback(
    async (localId: string) => {
      const item = videosRef.current.find(v => v.localId === localId)
      if (!item) return
      // Clean up any pending thumbnail blob URLs
      if (item.thumbnailUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.thumbnailUrl)
      }
      // Queued (create-flow) file that never uploaded: just drop it locally.
      if (pendingFilesRef.current.has(localId)) {
        pendingFilesRef.current.delete(localId)
        setHasPendingUploads(pendingFilesRef.current.size > 0)
        setVideos(prev => prev.filter(v => v.localId !== localId))
        return
      }
      if (item.ritualVideoId) {
        try {
          await deleteRitualVideo(ritualId, item.ritualVideoId)
        } catch (error) {
          toast.error(error instanceof Error ? error.message : "Failed to delete video")
          return
        }
      }
      setVideos(prev => prev.filter(v => v.localId !== localId))
    },
    [ritualId],
  )

  const hasActiveUpload = videos.some(v => v.status === "uploading" || v.status === "queued")

  return {
    videos,
    isVideoFile,
    uploadVideo,
    deleteVideo,
    setVideoTitle,
    setVideoThumbnail,
    flushPending,
    hasPendingUploads,
    hasActiveUpload,
  }
}
